/** Phase 7F — SCR-BIZ-022–031 invoices and reports bindings (07-02) */

export const INVOICES_REPORTS_SCREEN_IDS = [
  'SCR-BIZ-022',
  'SCR-BIZ-023',
  'SCR-BIZ-024',
  'SCR-BIZ-025',
  'SCR-BIZ-026',
  'SCR-BIZ-027',
  'SCR-BIZ-028',
  'SCR-BIZ-029',
  'SCR-BIZ-030',
  'SCR-BIZ-031',
] as const;

export type InvoicesReportsScreenId = (typeof INVOICES_REPORTS_SCREEN_IDS)[number];

/** Canonical URLs. Entity identity is in the path (`/invoices/:id`). */
export const INVOICES_REPORTS_ROUTES: Record<InvoicesReportsScreenId, string> = {
  'SCR-BIZ-022': '/invoice-list',
  'SCR-BIZ-023': '/invoices/:id',
  'SCR-BIZ-024': '/invoices/:id/query',
  'SCR-BIZ-025': '/invoices/:id/payment',
  'SCR-BIZ-026': '/spend-report',
  'SCR-BIZ-027': '/export-centre',
  'SCR-BIZ-028': '/account-settings',
  'SCR-BIZ-029': '/corporate-api-clients',
  'SCR-BIZ-030': '/account-activity',
  'SCR-BIZ-031': '/alerts-panel',
};

/** Pre-parity query-string routes — kept as redirects. */
export const INVOICES_REPORTS_LEGACY_ROUTES: Record<
  Extract<InvoicesReportsScreenId, 'SCR-BIZ-022' | 'SCR-BIZ-023' | 'SCR-BIZ-024' | 'SCR-BIZ-025'>,
  string
> = {
  'SCR-BIZ-022': '/invoice-list',
  'SCR-BIZ-023': '/invoice-detail',
  'SCR-BIZ-024': '/invoice-query',
  'SCR-BIZ-025': '/payment-record',
};

export const INVOICES_REPORTS_PERMISSIONS: Partial<
  Record<InvoicesReportsScreenId, readonly string[]>
> = {
  'SCR-BIZ-022': ['PERM-invoice.invoice.read'],
  'SCR-BIZ-023': ['PERM-invoice.invoice.read'],
  'SCR-BIZ-024': ['PERM-invoice.query.create'],
  'SCR-BIZ-025': ['PERM-invoice.payment.record'],
  'SCR-BIZ-026': ['PERM-report.corporate.read'],
  'SCR-BIZ-027': ['PERM-report.export.execute'],
  'SCR-BIZ-028': ['PERM-corporate.account.update'],
  'SCR-BIZ-029': ['PERM-identity.apiclient.create', 'PERM-identity.apiclient.revoke'],
  'SCR-BIZ-030': ['PERM-audit.log.read'],
  'SCR-BIZ-031': ['PERM-notification.history.read'],
};

export const INVOICES_REPORTS_ENDPOINTS: Partial<
  Record<InvoicesReportsScreenId, readonly string[]>
> = {
  'SCR-BIZ-022': [
    'GET /business/v1/invoices',
    'POST /business/v1/invoices/{id}/exports',
    'GET /business/v1/invoices/{id}/downloads',
  ],
  'SCR-BIZ-023': [
    'GET /business/v1/invoices/{id}',
    'GET /business/v1/invoices/{id}/lines',
    'GET /business/v1/invoices/{id}/queries',
    'POST /business/v1/invoices/{id}/exports',
    'GET /business/v1/invoices/{id}/downloads',
  ],
  'SCR-BIZ-024': [
    'GET /business/v1/invoices/{id}/lines',
    'POST /business/v1/invoices/{id}/queries',
  ],
  'SCR-BIZ-025': ['POST /business/v1/invoices/{id}/payments'],
  'SCR-BIZ-026': ['GET /business/v1/reports/{reportType}'],
  'SCR-BIZ-027': [
    'GET /business/v1/reports/exports',
    'POST /business/v1/reports/exports',
    'GET /business/v1/reports/runs/{runId}',
    'GET /business/v1/reports/runs/{runId}/output',
  ],
  'SCR-BIZ-028': ['GET /business/v1/account', 'PATCH /business/v1/account'],
  'SCR-BIZ-029': [
    'GET /business/v1/api-clients',
    'POST /business/v1/api-clients',
    'DELETE /business/v1/api-clients/{id}',
    'GET /business/v1/webhooks',
    'POST /business/v1/webhooks',
  ],
  'SCR-BIZ-030': ['GET /business/v1/activity', 'GET /business/v1/activity/export'],
  'SCR-BIZ-031': ['GET /business/v1/alerts', 'POST /business/v1/alerts/{id}/read'],
};

export const INVOICE_ID_STORAGE_KEY = 'beatq.business.invoiceId';

export const ALERTS_RETENTION_MONTHS = 24;
export const EXPORT_POLL_MS = 5_000;
export const ALERTS_POLL_MS = 15_000;

/** 10-06: employee-level spend dimensions additionally require this permission. */
export const EMPLOYEE_ORDERS_PERM = 'PERM-corporate.employee.orders';

/** 07-05 #52 category filter. Approval *requests* are still excluded in the inbox. */
export const ALERT_CATEGORY_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'BUDGET', label: 'Budget' },
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'POLICY', label: 'Policy' },
  { value: 'STATEMENT', label: 'Statements' },
  { value: 'MEMBERSHIP', label: 'Membership' },
  { value: 'APPROVAL', label: 'Approvals' },
];
